import React from 'react';
import { TaskyBadge } from '../../tasky';
import { SessionDetail } from '../../../services/api/adminBehaviorService';
import { DEVICE_LABELS, EVENT_TYPE_LABELS, formatDateTime, formatMs, formatTime } from './format';

interface Props {
  detail: SessionDetail;
}

const badgeVariant = (type: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'warm' => {
  switch (type) {
    case 'page_view':
      return 'default';
    case 'step':
    case 'action':
      return 'success';
    case 'api_timing':
      return 'warning';
    case 'api_error':
    case 'js_error':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const propsSummary = (props: Record<string, unknown> | null): string => {
  if (!props) return '';
  return Object.entries(props)
    .filter(([key]) => !['selector', 'tag'].includes(key))
    .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(' ');
};

/**
 * 1 セッションのイベントを時系列で表示する。直前からの経過時間と、page_leave の滞在時間を出す。
 */
const SessionTimeline: React.FC<Props> = ({ detail }) => {
  const { session, events } = detail;

  return (
    <div className="space-y-4">
      <div className="rounded-tasky-md border border-warm-border bg-warm-bg p-3 text-xs text-dark">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span>
            <span className="text-warm-text-muted">ユーザー: </span>
            {session.user_name ?? '匿名'}
            {session.user_email ? ` (${session.user_email})` : ''}
          </span>
          <span>
            <span className="text-warm-text-muted">組織: </span>
            {session.organization_name ?? '-'}
          </span>
          <span>
            <span className="text-warm-text-muted">開始: </span>
            {formatDateTime(session.started_at)}
          </span>
          <span>
            <span className="text-warm-text-muted">滞在: </span>
            {formatMs(session.duration_ms)}
          </span>
          <span>
            <span className="text-warm-text-muted">端末: </span>
            {DEVICE_LABELS[session.device_category ?? ''] ?? session.device_category ?? '-'} / {session.browser ?? '-'} /{' '}
            {session.os ?? '-'}
          </span>
        </div>
      </div>

      <ol className="space-y-1" data-testid="session-timeline">
        {events.map((event) => (
          <li
            key={event.id}
            className={`flex flex-col gap-1 rounded-tasky-sm border px-3 py-2 text-xs sm:flex-row sm:items-center sm:gap-3 ${
              event.event_type === 'api_error' || event.event_type === 'js_error'
                ? 'border-red-200 bg-red-50'
                : 'border-warm-border bg-white'
            }`}
          >
            <span className="w-20 shrink-0 font-mono text-warm-text-muted">{formatTime(event.occurred_at)}</span>
            <span className="w-16 shrink-0 font-mono text-warm-text-muted">+{formatMs(event.elapsed_ms)}</span>
            <TaskyBadge variant={badgeVariant(event.event_type)}>
              {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
            </TaskyBadge>
            <span className="min-w-0 flex-1 truncate text-dark">
              {event.event_name}
              {event.page_path && event.event_type !== 'page_view' && event.event_type !== 'page_leave' && (
                <span className="ml-2 text-warm-text-muted">@ {event.page_path}</span>
              )}
              {(event.event_type === 'page_view' || event.event_type === 'page_leave') && event.page_path && (
                <span className="ml-2 font-mono">{event.page_path}</span>
              )}
            </span>
            {event.duration_ms !== null && event.duration_ms !== undefined && (
              <span className="shrink-0 text-warm-text-muted">所要 {formatMs(event.duration_ms)}</span>
            )}
            {event.props && (
              <span className="hidden max-w-xs truncate text-warm-text-muted lg:inline" title={propsSummary(event.props)}>
                {propsSummary(event.props)}
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default SessionTimeline;
